import React from "react";
import { AlertCircle, CheckCircle2, LoaderCircle, Save } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useOutletContext, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getApplicantExtractedData } from "./homebuyerDocumentApi";
import "./HomebuyerExtractedData.css";

const EDITABLE_CONFIDENCE_THRESHOLD = 0.85;

const MONEY_FIELDS = new Set([
  "net_monthly_income",
  "annual_income",
  "taxable_income",
  "avg_monthly_income",
  "avg_monthly_expenses",
  "outstanding_loan_payments",
  "overdrafts",
]);

const DATE_FIELDS = new Set(["date_of_birth", "extraction_date", "tax_year"]);

const IBAN_FIELDS = new Set(["iban"]);

const ENUM_LABEL_FIELDS = new Set(["employment_status", "contract_type"]);

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMoney(value) {
  const parsed = parseNumber(value);
  if (parsed === null) return "";

  return `${parsed.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(parsed) ? 0 : 2,
    maximumFractionDigits: 2,
  })} €`;
}

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const raw = String(value).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-");
      return `${d}/${m}/${y}`;
    }

    return raw;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatIBAN(value) {
  if (!value) return "";
  return String(value)
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

function humanizeEnum(value) {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatFieldValue(fieldKey, value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "object") return JSON.stringify(value);

  if (MONEY_FIELDS.has(fieldKey)) {
    return formatMoney(value);
  }

  if (IBAN_FIELDS.has(fieldKey)) {
    return formatIBAN(value);
  }

  if (fieldKey === "extraction_date" || fieldKey === "date_of_birth") {
    return formatDate(value);
  }

  if (ENUM_LABEL_FIELDS.has(fieldKey)) {
    return humanizeEnum(value);
  }

  return String(value);
}

function toEditableValue(fieldKey, value) {
  if (value === null || value === undefined || value === "") return "";

  if (IBAN_FIELDS.has(fieldKey)) {
    return String(value).replace(/\s+/g, "").toUpperCase();
  }

  return String(value);
}

function buildDerivedForm(applicantPayload) {
  const form = applicantPayload?.form || {};
  const rawSections = applicantPayload?.raw_sections || {};
  const identityData = rawSections?.identity_data || {};
  const incomeData = rawSections?.income_data || {};
  const taxData = rawSections?.tax_declaration_data || {};
  const bankData = rawSections?.bank_statement_data || {};

  return {
    ...form,
    full_name: [form.first_name, form.last_name].filter(Boolean).join(" "),
    date_of_birth: form.date_of_birth || identityData.date_of_birth || "",
    address: form.official_address || identityData.official_address || "",
    national_id_number:
      form.national_id_number || identityData.national_id_number || "",
    nationality: form.nationality || identityData.nationality || "",
    social_security_number:
      form.social_security_number || identityData.social_security_number || "",
    national_register_number:
      form.national_register_number ||
      identityData.national_register_number ||
      "",
    employer_name: form.employer_name || incomeData.employer_name || "",
    employment_status:
      form.employment_status || incomeData.employment_status || "",
    contract_type: form.contract_type || incomeData.contract_type || "",
    net_monthly_income:
      form.net_monthly_income ?? incomeData.net_monthly_income ?? "",
    annual_income: form.annual_income ?? taxData.annual_income ?? "",
    taxable_income: form.taxable_income ?? taxData.taxable_income ?? "",
    tax_year: form.tax_year || taxData.tax_year || "",
    bank_name: form.bank_name || bankData.bank_name || "",
    iban: form.iban || bankData.iban || "",
    avg_monthly_income:
      form.avg_monthly_income ?? bankData.avg_monthly_income ?? "",
    avg_monthly_expenses:
      form.avg_monthly_expenses ?? bankData.avg_monthly_expenses ?? "",
    outstanding_loan_payments:
      form.outstanding_loan_payments ??
      bankData.outstanding_loan_payments ??
      "",
    overdrafts: form.overdrafts ?? bankData.overdrafts ?? "",
  };
}

function normalizeApplicantSections(applicantPayload, t) {
  const fieldSources = applicantPayload?.field_sources || {};
  const summary = applicantPayload?.summary || {};
  const derivedForm = buildDerivedForm(applicantPayload);

  const sections = [
    {
      key: "identity",
      title: t("sections.identity"),
      fields: [
        { key: "full_name", label: t("fields.fullName") },
        { key: "date_of_birth", label: t("fields.dateOfBirth") },
        { key: "national_id_number", label: t("fields.nationalIdNumber") },
        {
          key: "social_security_number",
          label: t("fields.socialSecurityNumber"),
        },
        {
          key: "national_register_number",
          label: t("fields.nationalRegisterNumber"),
        },
        { key: "address", label: t("fields.address") },
        { key: "nationality", label: t("fields.nationality") },
      ],
    },
    {
      key: "income",
      title: t("sections.income"),
      fields: [
        { key: "employer_name", label: t("fields.employer") },
        { key: "employment_status", label: t("fields.employmentStatus") },
        { key: "contract_type", label: t("fields.contractType") },
        { key: "net_monthly_income", label: t("fields.netMonthlyIncome") },
      ],
    },
    {
      key: "tax_declaration",
      title: t("sections.taxDeclaration"),
      fields: [
        { key: "tax_year", label: t("fields.taxYear") },
        { key: "taxable_income", label: t("fields.taxableIncome") },
        { key: "annual_income", label: t("fields.annualIncome") },
      ],
    },
    {
      key: "bank_statement",
      title: t("sections.bankStatement"),
      fields: [
        { key: "bank_name", label: t("fields.bankName") },
        { key: "iban", label: t("fields.iban") },
        { key: "avg_monthly_income", label: t("fields.avgMonthlyIncome") },
        { key: "avg_monthly_expenses", label: t("fields.avgMonthlyExpenses") },
        {
          key: "outstanding_loan_payments",
          label: t("fields.outstandingLoanPayments"),
        },
        { key: "overdrafts", label: t("fields.overdrafts") },
      ],
    },
  ];

  return {
    applicantId: applicantPayload?.application_applicant_id,
    role: applicantPayload?.role || "PRIMARY",
    fullName: applicantPayload?.full_name || "",
    summary,
    formValues: derivedForm,
    sections: sections
      .map((section) => ({
        ...section,
        fields: section.fields
          .map((field) => {
            const source = fieldSources[field.key] || {};
            const confidence =
              typeof source.confidence === "number" ? source.confidence : null;

            return {
              ...field,
              value: derivedForm[field.key] ?? "",
              confidence,
              editable:
                confidence !== null
                  ? confidence < EDITABLE_CONFIDENCE_THRESHOLD
                  : true,
            };
          })
          .filter((field) => field.value !== "" || field.confidence !== null),
      }))
      .filter((section) => section.fields.length > 0),
  };
}

function normalizePayloadToApplicants(payload) {
  if (Array.isArray(payload?.applicants)) {
    return payload.applicants;
  }

  if (payload?.application_applicant_id) {
    return [payload];
  }

  return [];
}

function getApplicantTitle(role, t, fullName) {
  const roleLabel =
    role === "CO_APPLICANT"
      ? t("applicants.coApplicant", "Co-applicant")
      : t("applicants.primaryApplicant", "Primary applicant");

  return fullName ? `${roleLabel} · ${fullName}` : roleLabel;
}

export default function HomebuyerExtractedData() {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation("homebuyer-extracted-data");
  const location = useLocation();
  const outlet = useOutletContext();

  const applicationId =
    outlet?.applicationId ?? location.state?.applicationId ?? null;

  const applicationApplicantId =
    outlet?.applicationApplicantId ??
    location.state?.applicationApplicantId ??
    null;

  const {
    currentStepKey,
    registerStepSubmitHandler,
    handleStepValidityChange,
    updateBorrowerSection,
    openSaveModal,
  } = outlet;

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [applicantCards, setApplicantCards] = React.useState([]);

  React.useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");

        const accessToken = await getAccessTokenSilently();

        const payload = await getApplicantExtractedData({
          applicationId,
          applicationApplicantId,
          accessToken,
        });

        console.log("EXTRACTED DATA RESPONSE:", payload);

        const applicants = normalizePayloadToApplicants(payload);
        const normalizedApplicants = applicants.map((applicantPayload) =>
          normalizeApplicantSections(applicantPayload, t),
        );

        setApplicantCards(normalizedApplicants);

        normalizedApplicants.forEach((applicant) => {
          if (applicant.role) {
            updateBorrowerSection(
              applicant.role,
              "extracted_data",
              applicant.formValues,
            );
          }
        });
      } catch (err) {
        setError(err.message || t("errors.loadFailed"));
      } finally {
        setLoading(false);
      }
    }

    if (applicationId) {
      loadData();
    } else {
      setLoading(false);
      setError(t("errors.missingApplication"));
    }
  }, [
    applicationId,
    applicationApplicantId,
    getAccessTokenSilently,
    updateBorrowerSection,
    t,
  ]);

  function handleFieldChange(applicantRole, fieldKey, value) {
    setApplicantCards((prev) =>
      prev.map((applicant) => {
        if (applicant.role !== applicantRole) return applicant;

        const nextFormValues = { ...applicant.formValues };

        if (fieldKey === "full_name") {
          const trimmed = value.trim();
          const [first_name, ...rest] = trimmed.split(" ");
          nextFormValues.full_name = value;
          nextFormValues.first_name = first_name || "";
          nextFormValues.last_name = rest.join(" ") || "";
        } else if (fieldKey === "address") {
          nextFormValues.address = value;
          nextFormValues.official_address = value;
        } else if (fieldKey === "iban") {
          nextFormValues[fieldKey] = value.replace(/\s+/g, "").toUpperCase();
        } else {
          nextFormValues[fieldKey] = value;
        }

        updateBorrowerSection(applicantRole, "extracted_data", nextFormValues);

        return {
          ...applicant,
          formValues: nextFormValues,
          sections: applicant.sections.map((section) => ({
            ...section,
            fields: section.fields.map((field) =>
              field.key === fieldKey ? { ...field, value } : field,
            ),
          })),
        };
      }),
    );
  }

  React.useEffect(() => {
    const isValid = !loading && !error && applicantCards.length > 0;

    const valuesByRole = applicantCards.reduce((acc, applicant) => {
      acc[applicant.role] = applicant.formValues;
      return acc;
    }, {});

    handleStepValidityChange(currentStepKey, isValid, {}, valuesByRole);
  }, [
    loading,
    error,
    applicantCards,
    currentStepKey,
    handleStepValidityChange,
  ]);

  React.useEffect(() => {
    async function handleExtractedStepSubmit() {
      return { ok: true };
    }

    registerStepSubmitHandler(currentStepKey, handleExtractedStepSubmit);

    return () => {
      registerStepSubmitHandler(currentStepKey, null);
    };
  }, [currentStepKey, registerStepSubmitHandler]);

  function formatScoreAsPercent(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "";

    const percent = parsed <= 1 ? parsed * 100 : parsed;

    return `${percent.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })}%`;
  }

  if (loading) {
    return (
      <div className="hb-extracted-page">
        <div className="hb-step-page__header">
          <h1 className="hb-step-page__title">{t("title")}</h1>
          <button
            type="button"
            className="hb-app-flow__save-btn"
            onClick={openSaveModal}
          >
            <Save size={16} />
            <span>{t("buttons.save")}</span>
          </button>
        </div>

        <div className="hb-extracted-empty">
          <LoaderCircle className="hb-spin" size={26} />
          <p>{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hb-extracted-page">
        <div className="hb-step-page__header">
          <h1 className="hb-step-page__title">{t("title")}</h1>
          <button
            type="button"
            className="hb-app-flow__save-btn"
            onClick={openSaveModal}
          >
            <Save size={16} />
            <span>{t("buttons.save")}</span>
          </button>
        </div>

        <div className="hb-extracted-alert">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="hb-extracted-page">
      <div className="hb-step-page__header">
        <h1 className="hb-step-page__title">{t("title")}</h1>

        <button
          type="button"
          className="hb-app-flow__save-btn"
          onClick={openSaveModal}
        >
          <Save size={16} />
          <span>{t("buttons.save")}</span>
        </button>
      </div>

      <div className="hb-extracted-shell">
        {applicantCards.map((applicant) => (
          <div
            key={applicant.applicantId || applicant.role}
            className="hb-extracted-applicant-block"
          >
            <div className="hb-extracted-summary">
              <div className="hb-extracted-summary__left">
                <div className="hb-extracted-summary__heading">
                  {t("summary.heading")}
                </div>

                <div className="hb-extracted-summary__badge">
                  <CheckCircle2 size={16} />
                  <span>{t("summary.verified")}</span>
                </div>

                <div className="hb-extracted-summary__info">
                  {t("summary.explanation")}
                </div>
              </div>

              <div className="hb-extracted-summary__stats">
                <div className="hb-extracted-stat">
                  <label>{t("summary.extractionDate")}</label>
                  <input
                    value={formatFieldValue(
                      "extraction_date",
                      applicant.summary?.extraction_date,
                    )}
                    readOnly
                  />
                </div>

                <div className="hb-extracted-stat">
                  <label>{t("summary.missingFields")}</label>
                  <input
                    value={formatFieldValue(
                      "missing_fields_count",
                      applicant.summary?.missing_fields_count,
                    )}
                    readOnly
                  />
                </div>

                <div className="hb-extracted-stat">
                  <label>{t("summary.modelConfidenceAvg")}</label>
                  <input
                    value={formatScoreAsPercent(
                      applicant.summary?.model_confidence_avg,
                    )}
                    readOnly
                  />
                </div>

                <div className="hb-extracted-stat">
                  <label>{t("summary.overallModelAccuracy")}</label>
                  <input
                    value={formatScoreAsPercent(
                      applicant.summary?.model_field_accuracy,
                    )}
                    readOnly
                  />
                </div>

                <div className="hb-extracted-stat">
                  <label>{t("summary.fieldConsistency")}</label>
                  <input
                    value={formatScoreAsPercent(
                      applicant.summary?.field_consistency,
                    )}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div className="hb-extracted-grid">
              {applicant.sections.map((section) => (
                <div
                  key={`${applicant.role}-${section.key}`}
                  className="hb-extracted-card"
                >
                  <h2 className="hb-extracted-card__title">{section.title}</h2>

                  <div className="hb-extracted-fields">
                    {section.fields.map((field) => (
                      <div
                        key={`${applicant.role}-${field.key}`}
                        className={`hb-extracted-field ${
                          field.editable ? "is-editable" : "is-readonly"
                        }`}
                      >
                        <label className="hb-extracted-field__label">
                          <span>{field.label}</span>
                          {field.confidence !== null ? (
                            <span className="hb-extracted-field__confidence">
                              {Math.round(field.confidence * 100)}%
                            </span>
                          ) : null}
                        </label>

                        {field.editable ? (
                          <input
                            value={formatFieldValue(
                              field.key,
                              applicant.formValues[field.key],
                            )}
                            onChange={(e) =>
                              handleFieldChange(
                                applicant.role,
                                field.key,
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          <input
                            value={formatFieldValue(
                              field.key,
                              applicant.formValues[field.key],
                            )}
                            readOnly
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="hb-extracted-note">{t("note")}</div>
      </div>
    </div>
  );
}
